import { useEffect, useMemo, useState } from "react";
import { FileDown, FileText } from "lucide-react";
import { apiBaseURL, serverApiOrigin } from "@/lib/api-config";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Public invoice portal — no auth required.
 * URL: `/#invoice-portal?token=<hex32|hex64>`
 */

function readTokenFromHash(): string | null {
  const h = typeof window !== "undefined" ? window.location.hash : "";
  if (!h) return null;
  const idx = h.indexOf("?");
  if (idx < 0) return null;
  const qs = new URLSearchParams(h.slice(idx + 1));
  const t = (qs.get("token") || "").trim();
  if (!t) return null;
  if (!/^[a-f0-9]{32}$|^[a-f0-9]{64}$/i.test(t)) return null;

  return t.toLowerCase();
}

function useIsMobileViewport(): boolean {
  const [mobile, setMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return mobile;
}

export function InvoicePortalPage() {
  const [token, setToken] = useState<string | null>(() => readTokenFromHash());
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobileViewport();

  useEffect(() => {
    const onHash = () => setToken(readTokenFromHash());
    window.addEventListener("hashchange", onHash);

    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const pdfUrl = useMemo(() => {
    if (!token) return null;
    const base = (apiBaseURL || serverApiOrigin || "").replace(/\/$/, "");

    return `${base}/api/public/invoices/${token}`;
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing invoice token.");
    } else {
      setError(null);
    }
  }, [token]);

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white px-4 py-3 pt-safe">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-sm font-bold text-white">
              RP
            </div>
            <div>
              <p className="font-semibold leading-tight">Invoice Portal</p>
              <p className="text-xs text-slate-500">Secure share link</p>
            </div>
          </div>
          {pdfUrl ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className={cn(buttonVariants({ variant: "default", size: "lg" }), "w-full sm:w-auto")}
              >
                <FileText className="mr-2 h-4 w-4" />
                View invoice
              </a>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className={cn(buttonVariants({ variant: "outline", size: "lg" }), "w-full sm:w-auto")}
              >
                <FileDown className="mr-2 h-4 w-4" />
                Download
              </a>
            </div>
          ) : null}
        </div>
      </header>

      <main className="flex flex-1 flex-col px-4 py-4 pb-safe">
        {error ? (
          <div className="mx-auto mt-8 max-w-lg rounded-xl border border-red-200 bg-white p-6 text-center text-red-700">
            {error}
          </div>
        ) : pdfUrl ? (
          isMobile ? (
            <div className="mx-auto flex w-full max-w-lg flex-1 flex-col items-center justify-center gap-4 rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <FileText className="h-12 w-12 text-slate-400" />
              <div>
                <p className="font-medium">Invoice ready</p>
                <p className="mt-1 text-sm text-slate-500">
                  Mobile browsers open PDFs best in a new tab. Tap below to view your invoice.
                </p>
              </div>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className={cn(buttonVariants({ size: "lg" }), "w-full")}
              >
                Open invoice PDF
              </a>
            </div>
          ) : (
            <iframe
              title="Invoice PDF"
              src={pdfUrl}
              className="mx-auto h-[calc(100dvh-11rem)] w-full max-w-5xl flex-1 rounded-xl border border-slate-200 bg-white"
            />
          )
        ) : (
          <p className="text-center text-slate-500">Loading invoice…</p>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white px-4 py-3 text-center text-xs text-slate-500 pb-safe">
        Powered by invoice.cprawat.com
      </footer>
    </div>
  );
}
